/**
 * Client-side block list — "deniable blocking" parity with Signal.
 *
 * The server NEVER learns who we've blocked. A blocked peer's outbound
 * messages still complete at the wire level (TCP/HTTPS 2xx, ratchet
 * advances on receive) so they cannot distinguish "blocked" from "user
 * has poor reception today." Enforcement happens at the RENDER step on
 * our device: render-suppression, no notification, no delivery/read
 * receipt emission, no typing-indicator response, no call ringing.
 *
 * Cross-device sync is via the existing pairwise E2EE channel to our
 * OWN linked devices — encoded as a `kaether:block:sync:v1:` control
 * payload. Sync is last-writer-wins by `ts` so a hostile server holding
 * back a recent `.block` and replaying an older `.unblock` cannot
 * resurrect a blocked peer: the `.block` (newer ts) wins on next sync.
 * Sync envelopes MUST be signature-verified by the caller before being
 * handed to `BlockListStore.apply`; the store does not re-check.
 *
 * This file: pure value types and the sync codec. Storage lives in
 * BlockListStore; enforcement lives in BlockEnforcementPolicy.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/BlockListEntry.swift`.
 */

// ---------------------------------------------------------------------------
// Value types
// ---------------------------------------------------------------------------

/**
 * Reason a peer was blocked. `userInitiated` is the default — the other
 * cases exist so a future Settings UI can offer per-reason context
 * ("auto-blocked because flagged as spam") without re-using a free-text
 * field that would be hard to migrate.
 */
export type BlockReason =
  | "userInitiated"
  | "spamFlag"
  | "calendarHarassment"
  | "other";

/** Sync action: a single payload either installs or removes a block. */
export type BlockAction = "block" | "unblock";

/**
 * Persistent entry for a single blocked userId.
 *
 * Stored one-record-per-userId in IndexedDB; keyed by `userId`.
 */
export interface BlockListEntry {
  /** Peer userId we have blocked. */
  readonly userId: string;
  /** Unix seconds when we blocked them. Used for ts-monotonic LWW apply. */
  readonly blockedAt: number;
  /** Why we blocked. Defaults to `userInitiated`. */
  readonly reason: BlockReason;
}

/**
 * JSON shape of a cross-device sync payload. Schema-versioned so we
 * can extend the wire format without breaking older linked devices.
 *
 * `reason` is present only on `.block`. `.unblock` carries null —
 * `BlockSyncCodec` enforces this on encode/decode.
 */
export interface BlockSyncPayload {
  readonly schema: number;
  readonly blockedUserId: string;
  readonly action: BlockAction;
  readonly ts: number;
  /** Present only on `action === "block"`. null on `"unblock"`. */
  readonly reason: BlockReason | null;
}

export const BLOCK_SYNC_SCHEMA_VERSION = 1;

// ---------------------------------------------------------------------------
// Codec error
// ---------------------------------------------------------------------------

export type BlockSyncCodecErrorKind =
  | { readonly tag: "notABlockSync" }
  | { readonly tag: "malformed"; readonly detail: string }
  | { readonly tag: "unsupportedSchema"; readonly version: number }
  | { readonly tag: "emptyUserId" }
  | { readonly tag: "missingReasonOnBlock" };

export class BlockSyncCodecError extends Error {
  constructor(public readonly kind: BlockSyncCodecErrorKind) {
    super(BlockSyncCodecError.message(kind));
    this.name = "BlockSyncCodecError";
  }

  private static message(kind: BlockSyncCodecErrorKind): string {
    switch (kind.tag) {
      case "notABlockSync":
        return "not a block-sync payload";
      case "malformed":
        return `malformed block-sync payload: ${kind.detail}`;
      case "unsupportedSchema":
        return `unsupported block-sync schema version: ${kind.version}`;
      case "emptyUserId":
        return "blockedUserId must not be empty";
      case "missingReasonOnBlock":
        return "reason is required on .block action";
    }
  }
}

// ---------------------------------------------------------------------------
// Codec
// ---------------------------------------------------------------------------

/**
 * Encode/decode cross-device sync payloads.
 *
 * Wire format: `kaether:block:sync:v1:<base64url(JSON)>`
 *
 * Never `stoat:` — prefix is always `kaether:`.
 */
export const BlockSyncCodec = {
  PREFIX: "kaether:block:sync:v1:" as const,

  isBlockSync(plaintext: string): boolean {
    return plaintext.startsWith(BlockSyncCodec.PREFIX);
  },

  encode(payload: BlockSyncPayload): string {
    if (!payload.blockedUserId) {
      throw new BlockSyncCodecError({ tag: "emptyUserId" });
    }
    if (payload.action === "block" && payload.reason === null) {
      throw new BlockSyncCodecError({ tag: "missingReasonOnBlock" });
    }
    const json = JSON.stringify(payload);
    return BlockSyncCodec.PREFIX + base64UrlEncode(json);
  },

  decode(plaintext: string): BlockSyncPayload {
    if (!plaintext.startsWith(BlockSyncCodec.PREFIX)) {
      throw new BlockSyncCodecError({ tag: "notABlockSync" });
    }
    const b64 = plaintext.slice(BlockSyncCodec.PREFIX.length);
    let json: string;
    try {
      json = base64UrlDecode(b64);
    } catch {
      throw new BlockSyncCodecError({
        tag: "malformed",
        detail: "invalid base64url",
      });
    }
    let payload: BlockSyncPayload;
    try {
      payload = JSON.parse(json) as BlockSyncPayload;
    } catch (e) {
      throw new BlockSyncCodecError({
        tag: "malformed",
        detail: `json parse failed: ${e}`,
      });
    }
    if (payload.schema !== BLOCK_SYNC_SCHEMA_VERSION) {
      throw new BlockSyncCodecError({
        tag: "unsupportedSchema",
        version: payload.schema,
      });
    }
    if (!payload.blockedUserId) {
      throw new BlockSyncCodecError({ tag: "emptyUserId" });
    }
    if (payload.action === "block" && payload.reason === null) {
      throw new BlockSyncCodecError({ tag: "missingReasonOnBlock" });
    }
    return payload;
  },

  /**
   * Convenience: returns null for non-block-sync plaintext. Throws only
   * for payloads that DID claim to be a block-sync (caller logs them).
   */
  tryDecode(plaintext: string): BlockSyncPayload | null {
    if (!BlockSyncCodec.isBlockSync(plaintext)) return null;
    return BlockSyncCodec.decode(plaintext);
  },
} as const;

// ---------------------------------------------------------------------------
// Base64url helpers (browser-native, no dependencies)
// ---------------------------------------------------------------------------

function base64UrlEncode(s: string): string {
  // TextEncoder → Uint8Array → btoa
  const bytes = new TextEncoder().encode(s);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function base64UrlDecode(s: string): string {
  // Pad, convert back from url-safe, atob → string
  let padded = s.replace(/-/g, "+").replace(/_/g, "/");
  const pad = (4 - (padded.length % 4)) % 4;
  padded += "=".repeat(pad);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new TextDecoder().decode(bytes);
}
